import { createUseStyles } from "react-jss";
import _ from "lodash";
import React, { memo, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import PropTypes from "prop-types";
import jssVariables from "../../../jss_setup";
import {
  CurrencyContext,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import { Button } from "../../../components/common/Button";
import tinycolor from "tinycolor2";
import { Placeholder } from "rsuite";
import { selectedCarRentalChange } from "../../../actions/search/car_rental_search";
import { DateTime } from "luxon";
import { Price } from "../../../components/common/Price";
import { InlineIcon } from "@iconify/react";

const bottomContainerStyles = createUseStyles({
  bottomContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content max-content",
    justifyContent: "space-between",
    alignItems: "center",
    padding: jssVariables.double_gap,
    background: tinycolor(jssVariables.colors.base_color)
      .setAlpha(0.05)
      .toRgbString(),
    borderRadius: `0 0 ${jssVariables.normal_gap} ${jssVariables.normal_gap}`,
    ..._.get(props, "customStyles.bottomContainer", {}),
  }),
  placeholder: (props) => ({
    width: "10rem!important",
    ..._.get(props, "customStyles.placeholder", {}),
  }),
  priceContr: (props) => ({
    textAlign: "left",
    textTransform: "capitalize",
    ..._.get(props, "customStyles.priceContr", {}),
  }),
  priceTitle: (props) => ({
    fontSize: "xx-large",
    fontWeight: "bold",
    lineHeight: "24px",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.priceTitle", {}),
  }),
  priceSubTitle: (props) => ({
    fontSize: "medium",
    fontWeight: "normal",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.priceSubTitle", {}),
  }),
  placeholderParagraph: (props) => ({
    width: "7rem!important",
    ..._.get(props, "customStyles.placeholderImg", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    bottomContainer: (props) => ({
      gridTemplateColumns: "1fr",
      gridTemplateRows: "1fr max-content",
      alignItems: "end",
      gridGap: jssVariables.half_gap,
      padding: jssVariables.double_gap,
      paddingLeft: jssVariables.double_gap,
      borderRadius: `0 ${jssVariables.normal_gap} ${jssVariables.normal_gap} 0`,
      ..._.get(props, "customStyles.bottomContainer.smallscreen", {}),
    }),
    priceContr: (props) => ({
      ..._.get(props, "customStyles.priceContr.smallscreen", {}),
    }),
    priceSubTitle: (props) => ({
      ..._.get(props, "customStyles.priceSubTitle.smallscreen", {}),
    }),
  },
});
export var BottomContainer = ({ ghost, carRental, customStyles, idx }) => {
  const { brand } = useContext(StyleTypeContext);
  const { currency: settedCurrency } = useContext(CurrencyContext);

  const { t } = useContext(TranslationContext);
  const classes = bottomContainerStyles({ customStyles });
  const history = useHistory();
  const dispatch = useDispatch();

  const { page_session_id, pick_up_date, drop_off_date } = useSelector(
    (state) => ({
      page_session_id: state.SessionIds.CAR.page_session_id,
      pick_up_date: state.CarRentalSearchForm.pick_up_date,
      drop_off_date: state.CarRentalSearchForm.drop_off_date,
    })
  );

  const onSelectCarRental = useCallback(
    ({ key, value }) => {
      dispatch(selectedCarRentalChange({ key, value }));
    },
    [dispatch]
  );

  async function onPreBook() {
    await onSelectCarRental({ key: "car_rental", value: { ...carRental } });
    history.push(`/car-rentals/booking_process/${page_session_id}`);
  }

  const pickUpDateTime = DateTime.fromISO(pick_up_date);
  const dropOffDateTime = DateTime.fromISO(drop_off_date);
  const differenceInDays = Math.round(
    dropOffDateTime.diff(pickUpDateTime, "days").days
  );

  return (
    <div className={classes.bottomContainer}>
      {ghost ? (
        <React.Fragment>
          <Placeholder.Paragraph
            rows={2}
            active
            className={classes.placeholderParagraph}
          />
          <Button ghost={true} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <div className={classes.priceContr}>
            <div className={classes.priceTitle}>
              <Price
                price={carRental.price.amount}
                currency={carRental.price.currency}
                styleless
              />
            </div>

            <div className={classes.priceSubTitle}>{`${t(
              "priceFor"
            )} ${differenceInDays} ${
              differenceInDays > 1 ? t("days") : t("day")
            }`}</div>
          </div>
          <Button
            id={`carRental_${idx}_selectBtn`}
            text={t("select")}
            showIcon={["rocs"].includes(brand) ? true : false}
            icon="akar-icons:arrow-right"
            size="large"
            onClick={onPreBook}
            customStyles={_.get(customStyles, "bookBtn", {})}
          />
        </React.Fragment>
      )}
    </div>
  );
};
BottomContainer.defaultProps = {
  ghost: false,
  carRental: {},
  customStyles: {},
};
BottomContainer.propTypes = {
  ghost: PropTypes.bool,
  carRental: PropTypes.object,
  customStyles: PropTypes.object,
};
BottomContainer = memo(BottomContainer);

const inclusionsStyles = createUseStyles({
  Inclusions: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    alignContent: "baseline",
    color: "#1B8800",
    borderRadius: ".25rem",
    ..._.get(props, "customStyles.Inclusions", {}),
  }),
  title: (props) => ({
    background: "rgba(27, 136, 0, 0.1)",
    color: "#1B8800",
    padding: [jssVariables.half_gap, jssVariables.normal_gap],
    width: "max-content",
    borderRadius: ".25rem",
    ..._.get(props, "customStyles.title", {}),
  }),
  inclusion: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr max-content",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.inclusion", {}),
  }),
  icon: (props) => ({
    fontSize: "large",
    ..._.get(props, "customStyles.icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    Inclusions: (props) => ({
      ..._.get(props, "customStyles.Inclusions.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const Inclusions = ({ carRental }) => {
  const classes = inclusionsStyles({});

  const { t } = useContext(TranslationContext);

  return (
    (carRental?.inclusions ?? []).length > 0 && (
      <div className={classes.Inclusions}>
        <div className={classes.title}>{t("inclusions")}</div>
        {carRental.inclusions.map((inclusion, idx) =>
          inclusion.coverage_type && inclusion.code !== "CF" ? (
            <div
              className={classes.inclusion}
              key={idx}
              style={{
                fontWeight: inclusion.amount > 0 ? "bold" : "normal",
              }}>
              <InlineIcon icon="ic:outline-check" className={classes.icon} />
              {inclusion.coverage_type}
              {inclusion.amount > 0 && (
                <Price
                  price={inclusion.amount}
                  currency={inclusion.currency}
                  styleless
                />
              )}
            </div>
          ) : null
        )}
      </div>
    )
  );
};
Inclusions.defaultProps = {
  carRental: {},
};
Inclusions.propTypes = {
  carRental: PropTypes.object.isRequired,
};
